import { type Registry as Services, service } from '@ember/service';
import type Transition from '@ember/routing/transition';
import {
  scrollRhsToTop,
  loadAiSettingsIfPermitted,
} from 'uplisting-frontend/pods/-sidebar/utils';
import PermittedRoute from 'uplisting-frontend/pods/base/permitted-route';
import { isV2OverlayRoute } from 'uplisting-frontend/utils/v2-page';
import ActionBookingsController from 'uplisting-frontend/pods/action/bookings/controller';
import type { ModelRegistry } from 'ember-data/types/registries/model';

export interface IParams extends Record<string, unknown> {
  booking_id: string;
}

export default abstract class SidebarBookingsRoute extends PermittedRoute {
  @service features!: Services['features'];

  @service('repositories/booking')
  bookingRepository!: Services['repositories/booking'];

  @service('calendar/manager')
  calendarManager!: Services['calendar/manager'];

  @service('booking-drawer')
  bookingDrawer!: Services['booking-drawer'];

  permission = 'calendar.bookings';

  get hasDrawerFooter(): boolean {
    return isV2OverlayRoute(this.routeName, this.features);
  }

  async model(params: IParams) {
    const [booking] = await Promise.all([
      this.bookingRepository.findRecord(params.booking_id, {
        reload: true,
        include: this.include,
      }),
      loadAiSettingsIfPermitted(this),
    ]);

    this.#keepPendingUnitMove(booking);

    return booking;
  }

  private get include(): string {
    const include = [
      'property.airbnb_official_link',
      'airbnb_official_booking',
      'airbnb_official_alteration_requests',
      'booking_payment',
      'booking_payment.payments',
      'booking_rental_agreement',
      'channel_user',
      'guest_identity_verification',
      'guest_invoices',
      'guest_invoices.additional_booking_charges',
      'guest_invoices.additional_booking_charges.tax',
      'guest_invoices.payment',
      'price',
      'price.discounts',
      'price.charges',
      'price_changes',
      'pending_additional_charges',
      'pending_additional_charges.tax',
      'property',
      'security_deposit',
      'security_deposit_action',
      'messages',
      'scheduled_messages',
      'scheduled_messages.automated_message_rule',
      'ai_occasion_context',
    ];

    if (
      this.features.isProtectionGateEnabled &&
      this.currentUser.hasAccessTo('disputes.index')
    ) {
      include.push('disputes');
    }

    if (this.features.isInboxAssignmentEnabled) {
      include.push('inbox_message');
    }

    return include.join(',');
  }

  setupController(
    controller: ActionBookingsController,
    model,
    transition: Transition,
  ): void {
    super.setupController(controller, model, transition);

    Object.assign(controller, { booking: model });

    if (this.hasDrawerFooter) {
      this.bookingDrawer.open(model);
    }

    if (model.airbnbOfficialBooking) {
      model.airbnbOfficialBooking.reload({
        adapterOptions: { refresh: true },
      });
    }

    scrollRhsToTop(this);
  }

  resetController(
    controller: ActionBookingsController,
    isExiting: boolean,
    transition: Transition,
  ): void {
    super.resetController(controller, isExiting, transition);

    if (!this.hasDrawerFooter) {
      return;
    }

    if (isExiting) {
      this.bookingDrawer.close();
    } else {
      this.bookingDrawer.discard();
    }
  }

  #keepPendingUnitMove(booking: ModelRegistry['booking']): void {
    const { dragOperation, dragChangedMultiUnit } =
      this.calendarManager.interaction;

    if (dragChangedMultiUnit && dragOperation?.record.model === booking) {
      booking.multiUnit = dragOperation.location as ModelRegistry['multi-unit'];
    }
  }
}
